import React from 'react';
import { getTelegramStatus, startTelegramLink, unlinkTelegram } from '@/api/api';
import { toast } from 'sonner';

const formatCountdown = (iso) => {
  if (!iso) return '';
  const end = Date.parse(iso);
  const now = Date.now();
  const ms = Math.max(0, end - now);
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

export default function TelegramSettings() {
  const [status, setStatus] = React.useState(null);
  const [linkToken, setLinkToken] = React.useState(null);
  const [pollId, setPollId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const loadStatus = React.useCallback(async () => {
    try {
      setLoading(true);
      const s = await getTelegramStatus();
      setStatus(s);
    } catch (e) {
      toast.error(e?.message || 'Failed to load Telegram status');
      setStatus({ linked: false });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { loadStatus(); }, [loadStatus]);

  const stopPolling = React.useCallback(() => {
    if (pollId) {
      clearInterval(pollId);
      setPollId(null);
    }
  }, [pollId]);

  const startPolling = React.useCallback(() => {
    stopPolling();
    const id = window.setInterval(async () => {
      try {
        const s = await getTelegramStatus();
        setStatus(s);
        if (s?.linked) {
          stopPolling();
          setLinkToken(null);
          toast.success('Telegram linked');
        } else if (linkToken && Date.now() > Date.parse(linkToken.expires_at)) {
          stopPolling();
          toast.error('Code expired. Generate a new link.');
        }
      } catch (e) {
        // transient errors ignored
      }
    }, 2500);
    setPollId(id);
  }, [stopPolling, linkToken]);

  const onStart = async () => {
    try {
      const token = await startTelegramLink();
      setLinkToken(token);
      if (token?.deep_link) {
        // open on user gesture
        window.open(token.deep_link, '_blank', 'noopener');
      }
      startPolling();
    } catch (e) {
      toast.error(e?.message || 'Failed to start linking');
    }
  };

  const onUnlink = async () => {
    try {
      await unlinkTelegram().catch(() => {});
      await loadStatus();
      toast.success('Unlinked');
    } catch (e) {
      // treat 404 as already unlinked
      await loadStatus();
    }
  };

  React.useEffect(() => () => stopPolling(), [stopPolling]);

  return (
    <div className="rounded border border-borderDefault p-4 bg-backgroundSurface space-y-3">
      <div>
        <div className="text-base font-semibold">Connect Telegram</div>
        <div className="text-sm text-textSecondary">Receive alerts directly in Telegram.</div>
      </div>

      {loading && <div className="text-sm text-textSecondary">Loading…</div>}

      {!loading && !status?.linked && (
        <div className="space-y-3">
          <div className="text-sm">Connect your Telegram to receive alerts.</div>
          {!linkToken ? (
            <button type="button" onClick={onStart} className="px-3 py-1.5 text-sm rounded-md border border-borderDefault bg-background hover:bg-muted">Generate link code</button>
          ) : (
            <div className="space-y-2">
              {linkToken?.deep_link && (
                <a href={linkToken.deep_link} target="_blank" rel="noreferrer" className="inline-block px-3 py-1.5 text-sm rounded-md border border-borderDefault bg-background hover:bg-muted">Open Telegram</a>
              )}
              <div className="text-sm">
                Tap Open Telegram, or send this message to the bot:
                <div className="mt-1 font-mono text-xs bg-muted inline-block px-2 py-1 rounded">/start {linkToken.code}</div>
              </div>
              <div className="text-xs text-textSecondary">Expires in: {formatCountdown(linkToken.expires_at)}</div>
              {linkToken.instructions && (
                <div className="text-xs text-textSecondary whitespace-pre-line">{linkToken.instructions}</div>
              )}
            </div>
          )}
        </div>
      )}

      {!loading && status?.linked && (
        <div className="space-y-2">
          <div className="text-sm">Linked to chat: <span className="font-mono">{status.chat_id}</span></div>
          <button type="button" onClick={onUnlink} className="px-3 py-1.5 text-sm rounded-md border border-borderDefault bg-background hover:bg-muted">Unlink</button>
        </div>
      )}
    </div>
  );
}
